import React, { useState } from 'react';
import { useCorrelations } from '@/hooks/useCorrelations';
import { Skeleton } from '@/components/ui/skeleton';

const DEFAULT_SYMBOLS = ['DXY', 'XAUUSD', 'BTCUSD', 'EURUSD', 'SPY', 'VIX'];

interface CorrelationHeatmapProps {
  heldSymbols?: string[];
  isLoading?: boolean;
}

function correlationColor(v: number): string {
  // green (-1) → neutral (0) → red (+1) using design system accent colors
  const clamped = Math.max(-1, Math.min(1, v));
  if (clamped < 0) {
    const t = Math.abs(clamped);
    // Blend from neutral gray to accent-green (hsl 142, 71%, 45% → approx 56, 162, 81)
    const r = Math.round(40 * (1 - t) + 56 * t);
    const g = Math.round(40 * (1 - t) + 162 * t);
    const b = Math.round(48 * (1 - t) + 81 * t);
    return `rgb(${r},${g},${b})`;
  } else {
    const t = clamped;
    // Blend from neutral gray to accent-red (hsl 0, 84%, 60% → approx 235, 68, 68)
    const r = Math.round(40 * (1 - t) + 235 * t);
    const g = Math.round(40 * (1 - t) + 68 * t);
    const b = Math.round(48 * (1 - t) + 68 * t);
    return `rgb(${r},${g},${b})`;
  }
}

export const CorrelationHeatmap: React.FC<CorrelationHeatmapProps> = ({ heldSymbols, isLoading }) => {
  const { getCorrelation, isLoading: corrLoading } = useCorrelations();
  const [hover, setHover] = useState<{ row: number; col: number; val: number } | null>(null);

  const symbols = (heldSymbols && heldSymbols.length >= 2 ? heldSymbols.slice(0, 6) : DEFAULT_SYMBOLS);
  const n = symbols.length;
  const size = 300;
  const labelSpace = 50;
  const cellSize = (size - labelSpace) / n;

  if (isLoading || corrLoading) {
    return <Skeleton className="w-[300px] h-[300px] rounded-lg mx-auto" />;
  }

  return (
    <div className="relative mx-auto" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        {symbols.map((s, i) => (
          <text
            key={`col-${i}`}
            x={labelSpace + i * cellSize + cellSize / 2}
            y={labelSpace - 6}
            textAnchor="end"
            fontSize={9}
            fill="hsl(210, 15%, 55%)"
            fontFamily="'JetBrains Mono', monospace"
            transform={`rotate(-45, ${labelSpace + i * cellSize + cellSize / 2}, ${labelSpace - 6})`}
          >
            {s.length > 6 ? s.slice(0, 6) : s}
          </text>
        ))}
        {symbols.map((s, i) => (
          <text
            key={`row-${i}`}
            x={labelSpace - 4}
            y={labelSpace + i * cellSize + cellSize / 2 + 3}
            textAnchor="end"
            fontSize={9}
            fill="hsl(210, 15%, 55%)"
            fontFamily="'JetBrains Mono', monospace"
          >
            {s.length > 6 ? s.slice(0, 6) : s}
          </text>
        ))}
        {symbols.map((rowSym, ri) =>
          symbols.map((colSym, ci) => {
            const val = ri === ci ? 1.0 : (getCorrelation(rowSym, colSym) ?? 0);
            return (
              <rect
                key={`${ri}-${ci}`}
                x={labelSpace + ci * cellSize}
                y={labelSpace + ri * cellSize}
                width={cellSize - 1}
                height={cellSize - 1}
                fill={correlationColor(val)}
                rx={2}
                onMouseEnter={() => setHover({ row: ri, col: ci, val })}
                onMouseLeave={() => setHover(null)}
                className="cursor-pointer transition-opacity"
                opacity={hover && (hover.row !== ri || hover.col !== ci) ? 0.7 : 1}
              />
            );
          })
        )}
      </svg>
      {hover && (
        <div
          className="absolute pointer-events-none bg-popover border border-border rounded-md px-2 py-1 text-xs text-popover-foreground z-10 font-mono-data"
          style={{
            left: labelSpace + hover.col * cellSize + cellSize / 2,
            top: labelSpace + hover.row * cellSize - 8,
            transform: 'translate(-50%, -100%)',
          }}
        >
          {symbols[hover.row]} / {symbols[hover.col]}: {hover.val.toFixed(3)}
        </div>
      )}
    </div>
  );
};
